/**
 * [API] 마이페이지
 * @module @apis/account
 */

import axios from '@/user/apis/common/axios-handle-error.service'
import moment from 'moment'

/**
 * 교육신청 조회
 * @method getAppliedLectures
 * @param {object<page:number, size:number>} options
 * @returns {promise} {data} = response or error
 */
export const getAppliedLectures = (options) => {
  return new Promise((resolve, reject) => {
    const sort1 = encodeURIComponent('startAt,desc')
    const sort2 = encodeURIComponent('appliedAt,desc')
    const params = `?page=${options.page}&size=${options.size}&sort=${sort1}&sort=${sort2}`
    const url = `/api/lecture/v1/applied-lectures`
    return axios
      .get(`${url}${params}`)
      .then(({ data }) => resolve(data))
      .catch(reject)
  })
}

/**
 * 교육완료 조회
 * @method getCompletedLectures
 * @param {object<page:number, size:number>} options
 * @returns {promise} {data} = response or error
 */
export const getCompletedLectures = (options) => {
  const sort = encodeURIComponent('finishedAt,desc')
  return new Promise((resolve, reject) => {
    const params = `?page=${options.page}&size=${options.size}&sort=${sort}`
    const url = `/api/lecture/v1/completed-lectures`
    return axios
      .get(`${url}${params}`)
      .then(({ data }) => resolve(data))
      .catch(reject)
  })
}

/**
 * 자격증 조회
 * @method getCertifications
 * @param {object<{ page, size }>} options - page, size
 * @returns {promise} {data} = response or error
 */
async function getCertifications({ page, size }) {
  return new Promise((resolve, reject) => {
    const params = `?page=${page}&size=${size}`
    const url = `/api/certification/v1/issued-certifications`

    return axios
      .get(`${url}${params}`)
      .then(({ data }) => resolve(data))
      .catch(reject)
  })
}

/**
 * 자격증 다운로드
 * @method downloadCertification
 * @param {object<certificationId:string>} options
 * @returns {promise} {data} = response or error
 */
export const downloadCertification = (options) => {
  return new Promise((resolve, reject) => {
    const url = `/api/certification/v1/issued-certifications/${options.certificationId}/download`
    const currentDate = moment(new Date()).format('YYYY-MM-DD')
    return axios
      .get(url, { responseType: 'blob' })
      .then((res) => {
        const { data, headers } = res
        const contentType = headers['content-type']
        const fileName = 'nhn-cloud-certified-' + currentDate
        const blob = new Blob([data], { type: contentType })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName
        link.click()
        link.remove()
        URL.revokeObjectURL(link.href)
        resolve(data)
      })
      .catch(reject)
  })
}

/**
 * 교육완료 수료증 다운로드
 * @method getCertificate
 * @param {object<lectureId:string, attenderId:string>} options
 * @returns {promise} {data} = response or error
 */
export const getCertificate = (options) => {
  return new Promise((resolve, reject) => {
    const url = `/api/lecture/v1/lectures/${options.lectureId}/attenders/${options.attenderId}/certificate`
    return axios
      .get(url, { responseType: 'blob' })
      .then((res) => {
        const { data, headers } = res
        const contentType = headers['content-type']
        const fileName = decodeURIComponent(headers['x-filename'])
        const blob = new Blob([data], { type: contentType })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName
        link.click()
        link.remove()
        URL.revokeObjectURL(link.href)
        resolve(data)
      })
      .catch(reject)
  })
}

async function getMaterialFile(options) {
  const url = `/api/lecture/v1/lectures/${options.lectureId}/attenders/${options.attenderId}/material-file`
  const { data, headers } = await axios.get(url, { responseType: 'blob' })
  const contentType = headers['content-type']
  const fileName = decodeURIComponent(headers['x-filename'])
  const blob = new Blob([data], { type: contentType })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = fileName
  link.click()
  link.remove()
  URL.revokeObjectURL(link.href)
  return data
}

/**
 * 교육신청 취소
 * @method deleteLectureCanceled
 * @param {object<lectureId:string, attenderId:string>} options
 * @returns {promise} {data} = response or error
 */
export const deleteLectureCanceled = (options) => {
  return new Promise((resolve, reject) => {
    const url = `/api/lecture/v1/lectures/${options.lectureId}/attenders/${options.attenderId}`
    return axios
      .delete(url)
      .then(({ data }) => resolve(data))
      .catch(reject)
  })
}

/**
 * 시험 일자 변경
 *
 * @param {Object} params - 시험 변경 파라미터
 * @param {string} params.currentExamNumber - 수험번호
 * @param {string|number} params.currentExamId - 현재 시험 ID
 * @param {string|number} params.changeExamId - 변경할 시험 ID
 * @returns {Promise} 시험 변경 결과
 */
export function changeCertificationExamDate({ currentExamNumber, currentExamId, changeExamId }) {
  return new Promise((resolve, reject) => {
    const url = `/api/certification/v1/applicants/${currentExamNumber}/change-exams`
    const body = {
      examId: currentExamId,
      targetExamId: changeExamId,
    }
    return axios
      .put(url, body)
      .then(({ data }) => resolve(data))
      .catch(reject)
  })
}

export function cancelCertificationExam({ examNumber, examId }) {
  return new Promise((resolve, reject) => {
    const url = `/api/certification/v1/exams/${examId}/applicants/${examNumber}`

    return axios
      .delete(url)
      .then(({ data }) => resolve(data))
      .catch(reject)
  })
}

export const AccountService = {
  getAppliedLectures,
  getCompletedLectures,
  getCertifications,
  downloadCertification,
  getCertificate,
  deleteLectureCanceled,
  getMaterialFile,
  changeCertificationExamDate,
  cancelCertificationExam,
}
